import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assignAdminVoicePhoneNumber,
  cutoverAdminVoicePhoneRouting,
  fetchAdminVapiToolRegistryDebug,
  fetchAdminVoicePhoneNumberHistory,
  fetchAdminVoicePhoneNumbers,
  inspectAdminVoicePhoneRouting,
  releaseAdminVoicePhoneNumber,
  rollbackAdminVoicePhoneRouting,
  searchAdminBusinesses,
  syncAdminVapiTools,
  syncAdminTwilioPhoneNumbers,
  syncAdminVapiPhoneNumbers,
  validateAdminVoicePhoneAssignment,
  verifyAdminVoicePhoneRouting,
} from "../api/admin";
import type { AdminVoicePhoneNumbersParams } from "../api/admin";

const phoneNumbersKey = (params: AdminVoicePhoneNumbersParams) => [
  "admin",
  "voice-phone-numbers",
  params,
] as const;

const vapiToolsKey = ["admin", "voice-vapi-tools"] as const;

export function useAdminVoicePhoneNumbers(params: AdminVoicePhoneNumbersParams) {
  return useQuery({
    queryKey: phoneNumbersKey(params),
    queryFn: () => fetchAdminVoicePhoneNumbers(params),
  });
}

export function useSyncAdminTwilioPhoneNumbers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) => syncAdminTwilioPhoneNumbers(force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "voice-phone-numbers"] });
    },
  });
}

export function useSyncAdminVapiPhoneNumbers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) => syncAdminVapiPhoneNumbers(force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "voice-phone-numbers"] });
    },
  });
}

export function useAdminVapiToolRegistryDebug() {
  return useQuery({
    queryKey: vapiToolsKey,
    queryFn: fetchAdminVapiToolRegistryDebug,
  });
}

export function useSyncAdminVapiTools() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) => syncAdminVapiTools(force),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: vapiToolsKey });
    },
  });
}

export function useAdminBusinessSearch(query: string) {
  return useQuery({
    queryKey: ["admin", "business-search", query],
    queryFn: () => searchAdminBusinesses(query, 20),
    enabled: query.trim().length >= 2,
  });
}

export function useValidateAdminVoicePhoneAssignment() {
  return useMutation({
    mutationFn: (payload: {
      voicePhoneNumberId: string;
      businessId: string;
      confirmRegionWarning?: boolean;
      allowCooldownOverride?: boolean;
    }) =>
      validateAdminVoicePhoneAssignment(payload.voicePhoneNumberId, {
        business_id: payload.businessId,
        confirm_region_warning: payload.confirmRegionWarning ?? false,
        allow_cooldown_override: payload.allowCooldownOverride ?? false,
      }),
  });
}

export function useAssignAdminVoicePhoneNumber() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      voicePhoneNumberId: string;
      businessId: string;
      confirmRegionWarning?: boolean;
      allowCooldownOverride?: boolean;
      reason?: string;
    }) =>
      assignAdminVoicePhoneNumber(payload.voicePhoneNumberId, {
        business_id: payload.businessId,
        confirm_region_warning: payload.confirmRegionWarning ?? false,
        allow_cooldown_override: payload.allowCooldownOverride ?? false,
        reason: payload.reason,
      }),
    onSettled: (_result, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "voice-phone-numbers"] });
      if (variables.voicePhoneNumberId) {
        queryClient.invalidateQueries({
          queryKey: ["admin", "voice-phone-number-history", variables.voicePhoneNumberId],
        });
      }
    },
  });
}

export function useReleaseAdminVoicePhoneNumber() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      voicePhoneNumberId: string;
      reason: string;
      cooldownDays?: number;
    }) =>
      releaseAdminVoicePhoneNumber(payload.voicePhoneNumberId, {
        reason: payload.reason,
        cooldown_days: payload.cooldownDays ?? 30,
      }),
    onSettled: (_result, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "voice-phone-numbers"] });
      if (variables.voicePhoneNumberId) {
        queryClient.invalidateQueries({
          queryKey: ["admin", "voice-phone-number-history", variables.voicePhoneNumberId],
        });
      }
    },
  });
}

export function useAdminVoicePhoneNumberHistory(voicePhoneNumberId: string | null) {
  return useQuery({
    queryKey: ["admin", "voice-phone-number-history", voicePhoneNumberId],
    queryFn: () => fetchAdminVoicePhoneNumberHistory(voicePhoneNumberId!),
    enabled: Boolean(voicePhoneNumberId),
  });
}

function invalidateNumberState(
  queryClient: ReturnType<typeof useQueryClient>,
  voicePhoneNumberId: string
) {
  queryClient.invalidateQueries({ queryKey: ["admin", "voice-phone-numbers"] });
  queryClient.invalidateQueries({
    queryKey: ["admin", "voice-phone-number-history", voicePhoneNumberId],
  });
}

export function useInspectAdminVoicePhoneRouting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (voicePhoneNumberId: string) =>
      inspectAdminVoicePhoneRouting(voicePhoneNumberId),
    onSettled: (_result, _error, voicePhoneNumberId) => {
      invalidateNumberState(queryClient, voicePhoneNumberId);
    },
  });
}

export function useCutoverAdminVoicePhoneRouting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      voicePhoneNumberId: string;
      confirmRegionWarning?: boolean;
    }) =>
      cutoverAdminVoicePhoneRouting(payload.voicePhoneNumberId, {
        confirm_region_warning: payload.confirmRegionWarning ?? false,
      }),
    onSettled: (_result, _error, variables) => {
      invalidateNumberState(queryClient, variables.voicePhoneNumberId);
    },
  });
}

export function useVerifyAdminVoicePhoneRouting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (voicePhoneNumberId: string) =>
      verifyAdminVoicePhoneRouting(voicePhoneNumberId),
    onSettled: (_result, _error, voicePhoneNumberId) => {
      invalidateNumberState(queryClient, voicePhoneNumberId);
    },
  });
}

export function useRollbackAdminVoicePhoneRouting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { voicePhoneNumberId: string; reason?: string }) =>
      rollbackAdminVoicePhoneRouting(payload.voicePhoneNumberId, {
        reason: payload.reason,
      }),
    onSettled: (_result, _error, variables) => {
      invalidateNumberState(queryClient, variables.voicePhoneNumberId);
    },
  });
}
